import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
}

export const AboutPageSEO: React.FC<SEOProps> = ({
  title = "Dr. Hirak Pahari | Liver Transplant Specialist | Max Super Speciality Hospital Delhi",
  description = "Dr. Hirak Pahari (MBBS, DNB, FMAS, FIAGES, FALS, FHBLT) is Senior Consultant – Liver Transplant and Hepatobiliary Surgeon at Max Super Speciality Hospital (Shalimar Bagh & Patparganj), Delhi. Specialized in liver cirrhosis, hepatitis B & C, acute liver failure, hepatocellular carcinoma, and intestinal transplant procedures.",
  canonicalUrl = "https://drhirakpahari.com/about",
  ogImage = "https://drhirakpahari.com/images/dr-hirak-pahari.jpg",
  ogType = "profile",
  keywords = "Dr. Hirak Pahari, liver transplant Delhi, liver specialist, hepatobiliary surgeon, Max Hospital, Max Super Speciality Hospital, Shalimar Bagh, Patparganj, liver cirrhosis treatment, hepatitis treatment, liver cancer specialist, intestinal transplant, hepatocellular carcinoma, MBBS, DNB, ASTS Fellowship, surgeon profile, liver doctor"
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Dr. Hirak Pahari - Liver Transplant Specialist" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Dr. Hirak Pahari | Liver Specialist | Delhi" />
      <meta name="twitter:description" content="Senior Consultant liver transplant and hepatobiliary surgeon at Max Super Speciality Hospital, Delhi. Specialized in liver diseases treatment." />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Rich Snippets Schema for Physician Profile */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Physician",
            "name": "Dr. Hirak Pahari",
            "image": "${ogImage}",
            "url": "${canonicalUrl}",
            "telephone": "+917827039028",
            "email": "hirak.pahari@gmail.com",
            "description": "${description}",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Max Super Speciality Hospital (Shalimar Bagh & Patparganj)",
              "addressLocality": "Delhi",
              "postalCode": "110088",
              "addressCountry": "IN"
            },
            "medicalSpecialty": [
              "Liver Transplant",
              "Hepatobiliary Surgery",
              "Intestinal Transplant"
            ],
            "hasCredential": [
              "MBBS",
              "DNB",
              "FMAS",
              "FIAGES",
              "FALS",
              "FHBLT"
            ],
            "availableService": [
              {
                "@type": "MedicalProcedure",
                "name": "Liver Transplant",
                "description": "Liver transplant surgery in Delhi"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Hepatobiliary Surgery",
                "description": "Specialized surgery for liver and biliary conditions"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Intestinal Transplant",
                "description": "Intestinal transplant procedures in Delhi"
              }
            ],
            "worksFor": {
              "@type": "Hospital",
              "name": "Max Super Speciality Hospital",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Delhi",
                "addressCountry": "India"
              }
            }
          }
        `}
      </script>
    </Helmet>
  );
};

export const HomePageSEO: React.FC<SEOProps> = ({
  title = "Dr. Hirak Pahari | Liver Transplant & Surgical Gastroenterology | Delhi",
  description = "Dr. Hirak Pahari provides expert liver transplant, hepatobiliary, and GI surgical services at Max Super Speciality Hospital (Shalimar Bagh & Patparganj), Delhi. Book online or in-person consultations for liver diseases, hepatitis, cirrhosis, liver cancer, and intestinal conditions.",
  canonicalUrl = "https://drhirakpahari.com",
  ogImage = "https://drhirakpahari.com/images/dr-hirak-pahari.jpg",
  ogType = "website",
  keywords = "Dr. Hirak Pahari, liver transplant Delhi, liver specialist, hepatobiliary surgeon, Max Hospital, Max Super Speciality Hospital Shalimar Bagh, Max Patparganj, liver cirrhosis treatment, online liver consultation, liver transplant surgeon, intestinal transplant, hepatitis treatment, liver doctor, best liver surgeon Delhi"
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Dr. Hirak Pahari - Liver Transplant Specialist" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Dr. Hirak Pahari | Liver Specialist | Delhi" />
      <meta name="twitter:description" content="Senior Consultant liver transplant and hepatobiliary surgeon at Max Super Speciality Hospital, Delhi. Book appointments online or offline." />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Rich Snippets Schema for Medical Professional & Services */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": "Dr. Hirak Pahari - Liver Transplant Specialist",
            "image": "${ogImage}",
            "url": "${canonicalUrl}",
            "telephone": "+917827039028",
            "email": "hirak.pahari@gmail.com",
            "description": "${description}",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Max Super Speciality Hospital (Shalimar Bagh & Patparganj)",
              "addressLocality": "Delhi",
              "postalCode": "110088",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "28.638458",
              "longitude": "77.186981"
            },
            "medicalSpecialty": [
              "Liver Transplant",
              "Hepatobiliary Surgery",
              "Intestinal Transplant",
              "Surgical Gastroenterology"
            ],
            "availableService": [
              {
                "@type": "MedicalProcedure",
                "name": "Liver Transplant",
                "description": "Liver transplant surgery in Delhi"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Hepatobiliary Surgery",
                "description": "Specialized surgery for liver and biliary conditions"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Cirrhosis Management",
                "description": "Treatment for liver cirrhosis"
              },
              {
                "@type": "MedicalProcedure",
                "name": "Hepatitis Treatment",
                "description": "Treatment for Hepatitis B and C"
              }
            ],
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday"
              ],
              "opens": "09:00",
              "closes": "17:00"
            },
            "potentialAction": {
              "@type": "ReserveAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "${canonicalUrl}#contact-info",
                "inLanguage": "en-IN",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/MobileWebPlatform"
                ]
              },
              "result": {
                "@type": "Reservation",
                "name": "Book appointment with Dr. Hirak Pahari"
              }
            }
          }
        `}
      </script>
    </Helmet>
  );
};


export const ServicesPageSEO: React.FC<SEOProps> = ({
  title = "Gastrointestinal & Liver Treatment Services | Dr. Hirak Pahari | Delhi",
  description = "Expert surgical care and advanced treatments for various gastrointestinal and hepatobiliary conditions by Dr. Hirak Pahari at Max Super Speciality Hospital, Delhi. Specialized services include liver transplant, hepatitis treatment, cirrhosis management, and intestinal procedures.",
  canonicalUrl = "https://drhirakpahari.com/services",
  ogImage = "https://drhirakpahari.com/images/dr-hirak-pahari-services.jpg",
  ogType = "website",
  keywords = "liver transplant Delhi, hepatobiliary surgery, liver cirrhosis treatment, hepatitis B treatment, hepatitis C treatment, intestinal transplant, acute liver failure, hepatocellular carcinoma, gastrointestinal surgery, Dr. Hirak Pahari, liver specialist services, Max Hospital, Max Super Speciality Hospital"
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Dr. Hirak Pahari - Liver Transplant Specialist" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Liver & GI Treatment Services | Dr. Hirak Pahari | Delhi" />
      <meta name="twitter:description" content="Expert surgical care and advanced treatments for gastrointestinal and hepatobiliary conditions at Max Super Speciality Hospital, Delhi." />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Rich Snippets Schema for Medical Services */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "about": {
              "@type": "MedicalSpecialty",
              "name": "Hepatology and Gastroenterology"
            },
            "name": "Treatment Services - Dr. Hirak Pahari",
            "headline": "Gastrointestinal & Liver Treatment Services",
            "description": "${description}",
            "url": "${canonicalUrl}",
            "image": "${ogImage}",
            "provider": {
              "@type": "Physician",
              "name": "Dr. Hirak Pahari",
              "image": "https://drhirakpahari.com/images/dr-hirak-pahari.jpg",
              "url": "https://drhirakpahari.com/about",
              "telephone": "+917827039028",
              "email": "hirak.pahari@gmail.com",
              "medicalSpecialty": [
                "Liver Transplant",
                "Hepatobiliary Surgery",
                "Intestinal Transplant"
              ],
              "hasCredential": [
                "MBBS",
                "DNB",
                "FMAS",
                "FIAGES",
                "FALS",
                "FHBLT"
              ],
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Max Super Speciality Hospital (Shalimar Bagh & Patparganj)",
                "addressLocality": "Delhi",
                "postalCode": "110088",
                "addressCountry": "IN"
              }
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Liver Transplant",
                    "description": "Surgical procedure to replace a diseased liver with a healthy liver from a donor."
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Hepatobiliary Surgery",
                    "description": "Surgical procedures involving the liver, bile ducts, and gallbladder."
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Cirrhosis Management",
                    "description": "Treatment of liver cirrhosis including medication, lifestyle changes, and surgical options."
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Hepatitis Treatment",
                    "description": "Treatment for Hepatitis B and C using latest antiviral medications and management protocols."
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 5,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Intestinal Transplant",
                    "description": "Surgical procedure for patients with intestinal failure who cannot absorb nutrients."
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 6,
                  "item": {
                    "@type": "MedicalProcedure",
                    "name": "Liver Cancer Treatment",
                    "description": "Comprehensive treatment for hepatocellular carcinoma and other liver cancers."
                  }
                }
              ]
            },
            "specialty": {
              "@type": "MedicalSpecialty",
              "name": "Hepatology"
            },
            "potentialAction": {
              "@type": "ReserveAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://drhirakpahari.com/contact",
                "inLanguage": "en-IN",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/MobileWebPlatform"
                ]
              },
              "result": {
                "@type": "Reservation",
                "name": "Book appointment for treatment services with Dr. Hirak Pahari"
              }
            }
          }
        `}
      </script>
    </Helmet>
  );
};


export const ContactPageSEO: React.FC<SEOProps> = ({
  title = "Book Consultation | Dr. Hirak Pahari | Liver Specialist | Delhi",
  description = "Schedule an appointment with Dr. Hirak Pahari at Max Super Speciality Hospital (Shalimar Bagh & Patparganj), Delhi. Expert consultation for liver diseases, transplants, and hepatobiliary conditions. Contact via phone, email, or online form for liver disease treatment.",
  canonicalUrl = "https://drhirakpahari.com/contact",
  ogImage = "https://drhirakpahari.com/images/dr-hirak-pahari-clinic.jpg",
  ogType = "website",
  keywords = "Dr. Hirak Pahari appointment, liver specialist consultation Delhi, book liver doctor, hepatologist appointment, liver transplant consultation, Max Hospital doctor booking, Max Super Speciality Hospital consultation, liver disease treatment Delhi, hepatobiliary surgeon appointment, online liver consultation"
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Dr. Hirak Pahari - Liver Transplant Specialist" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Book Appointment with Dr. Hirak Pahari | Delhi" />
      <meta name="twitter:description" content="Schedule a consultation with liver transplant specialist Dr. Hirak Pahari at Max Super Speciality Hospital, Delhi. Online and in-person appointments available." />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Rich Snippets Schema for Contact Page */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "MedicalClinic",
            "name": "Dr. Hirak Pahari - Liver Transplant Clinic",
            "image": "${ogImage}",
            "url": "${canonicalUrl}",
            "telephone": "+917827039028",
            "email": "hirak.pahari@gmail.com",
            "description": "${description}",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Max Super Speciality Hospital (Shalimar Bagh & Patparganj)",
              "addressLocality": "Delhi",
              "postalCode": "110088",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "28.638458",
              "longitude": "77.186981"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday"
                ],
                "opens": "09:00",
                "closes": "17:00"
              }
            ],
            "specialOpeningHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Sunday",
              "opens": "00:00",
              "closes": "00:00",
              "validFrom": "2024-01-01",
              "validThrough": "2025-12-31"
            },
            "department": {
              "@type": "MedicalSpecialty",
              "name": "Hepatology and Liver Transplant"
            },
            "medicalSpecialty": [
              "Liver Transplant",
              "Hepatobiliary Surgery",
              "Cirrhosis Management",
              "Hepatitis B and C Treatment",
              "Acute Liver Failure",
              "Hepatocellular Carcinoma"
            ],
            "availableService": [
              {
                "@type": "MedicalProcedure",
                "name": "Liver Transplant Consultation",
                "description": "Initial and follow-up consultations for liver transplant candidates and recipients"
              },
              {
                "@type": "MedicalTest",
                "name": "Liver Function Assessment",
                "description": "Comprehensive evaluation of liver health and function"
              },
              {
                "@type": "MedicalTherapy",
                "name": "Hepatitis Management",
                "description": "Treatment protocols for viral hepatitis B and C"
              }
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+917827039028",
              "contactType": "appointment",
              "availableLanguage": ["en", "hi"]
            },
            "potentialAction": {
              "@type": "ReserveAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "${canonicalUrl}",
                "inLanguage": "en-IN",
                "actionPlatform": [
                  "http://schema.org/DesktopWebPlatform",
                  "http://schema.org/MobileWebPlatform"
                ]
              },
              "result": {
                "@type": "Reservation",
                "name": "Book consultation with Dr. Hirak Pahari"
              }
            }
          }
        `}
      </script>
    </Helmet>
  );
};

export const BlogsPageSEO: React.FC<SEOProps> = ({
    title = "Medical Insights & Updates | Dr. Hirak Pahari | Liver Specialist Delhi",
    description = "Expert perspectives on surgical gastroenterology, transplantation, advanced liver procedures, and hepatobiliary conditions by Dr. Hirak Pahari at Max Super Speciality Hospital, Delhi. Latest medical insights and treatment updates for liver diseases.",
    canonicalUrl = "https://drhirakpahari.com/blogs",
    ogImage = "https://drhirakpahari.com/images/dr-hirak-pahari-blogs.jpg",
    ogType = "website",
    keywords = "liver transplant blog, hepatobiliary surgery insights, liver disease treatment, Dr. Hirak Pahari blog, liver cirrhosis information, hepatitis B treatment updates, hepatitis C management, liver cancer research, Max Hospital liver department, intestinal transplant insights, surgical gastroenterology articles, Delhi liver specialist blog, liver health updates, transplantation advancements"
  }) => {
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content={keywords} />
        
        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Dr. Hirak Pahari - Liver Transplant Specialist" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Medical Insights & Updates | Dr. Hirak Pahari" />
        <meta name="twitter:description" content="Expert medical articles on liver diseases, transplantation, and advanced surgical procedures by Dr. Hirak Pahari at Max Super Speciality Hospital, Delhi." />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Rich Snippets Schema for Blog Page */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Blog",
              "name": "Medical Insights & Updates - Dr. Hirak Pahari",
              "headline": "Expert Insights on Liver Treatment & Transplantation",
              "description": "${description}",
              "url": "${canonicalUrl}",
              "image": "${ogImage}",
              "author": {
                "@type": "Person",
                "name": "Dr. Hirak Pahari",
                "jobTitle": "Liver Transplant & Hepatobiliary Surgeon",
                "image": "https://drhirakpahari.com/images/dr-hirak-pahari.jpg",
                "url": "https://drhirakpahari.com/about",
                "sameAs": [
                  "https://www.linkedin.com/in/drhirakpahari",
                  "https://twitter.com/drhirakpahari"
                ]
              },
              "publisher": {
                "@type": "Organization",
                "name": "Dr. Hirak Pahari - Liver Specialist",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://drhirakpahari.com/images/logo.png"
                }
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "${canonicalUrl}"
              },
              "specialty": {
                "@type": "MedicalSpecialty",
                "name": "Hepatology"
              },
              "keywords": [
                "liver transplant",
                "hepatobiliary surgery",
                "liver disease treatment",
                "cirrhosis management",
                "hepatitis treatment",
                "intestinal transplant",
                "surgical gastroenterology"
              ],
              "about": {
                "@type": "MedicalCondition",
                "name": "Liver Diseases",
                "alternateName": "Hepatic Diseases"
              },
              "potentialAction": {
                "@type": "ReadAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "${canonicalUrl}/{blogId}",
                  "actionPlatform": [
                    "http://schema.org/DesktopWebPlatform",
                    "http://schema.org/MobileWebPlatform"
                  ]
                }
              }
            }
          `}
        </script>
      </Helmet>
    );
  };